/**
 * Time Entry service
 * Handles Firestore TimeEntries collection operations
 */

import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { TimeEntry } from '../../../shared/types';
import { db, functions } from '../config/firebase';
import { convertDocumentTimestamps, toDateNullable } from '@shared/utils/firestore';

function convertManagerAdjustments(raw: Record<string, unknown>): Record<string, unknown> {
  if (!raw.managerAdjustments || typeof raw.managerAdjustments !== 'object') return raw;
  const adj = raw.managerAdjustments as Record<string, unknown>;
  return {
    ...raw,
    managerAdjustments: {
      ...adj,
      approvedAt: toDateNullable(adj.approvedAt),
      originalClockIn: toDateNullable(adj.originalClockIn),
      originalClockOut: toDateNullable(adj.originalClockOut),
      correctedClockIn: toDateNullable(adj.correctedClockIn),
      correctedClockOut: toDateNullable(adj.correctedClockOut),
    },
  };
}

/**
 * Get time entries for specific staff members
 * Requires companyId for security rules compliance
 */
export const getStaffTimeEntries = async (
  staffIds: string[],
  companyId: string,
  startDate?: Date,
  endDate?: Date,
  managerId?: string
): Promise<TimeEntry[]> => {
  if (staffIds.length === 0 || !companyId) {
    return [];
  }

  const timeEntries: TimeEntry[] = [];

  if (managerId) {
    // Query by managerId — matches Firestore rule for managers (no get() call needed)
    const queryConstraints: QueryConstraint[] = [
      where('managerId', '==', managerId),
      orderBy('actualClockIn', 'desc'),
    ];
    if (startDate) queryConstraints.push(where('actualClockIn', '>=', startDate));
    if (endDate) queryConstraints.push(where('actualClockIn', '<=', endDate));

    const snapshot = await getDocs(query(collection(db, 'TimeEntries'), ...queryConstraints));
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertDocumentTimestamps<TimeEntry>(convertManagerAdjustments(doc.data())),
    })) as TimeEntry[];
  }

  // Company-level query (company admin / super admin)
  for (let i = 0; i < staffIds.length; i += 10) {
    const chunk = staffIds.slice(i, i + 10);
    const queryConstraints: QueryConstraint[] = [
      where('companyId', '==', companyId),
      where('userId', 'in', chunk),
      orderBy('actualClockIn', 'desc'),
    ];
    if (startDate) queryConstraints.push(where('actualClockIn', '>=', startDate));
    if (endDate) queryConstraints.push(where('actualClockIn', '<=', endDate));

    const snapshot = await getDocs(query(collection(db, 'TimeEntries'), ...queryConstraints));
    timeEntries.push(...snapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertDocumentTimestamps<TimeEntry>(convertManagerAdjustments(doc.data())),
    })) as TimeEntry[]);
  }

  return timeEntries.sort((a, b) => b.actualClockIn.getTime() - a.actualClockIn.getTime());
};

/**
 * Approve a time entry
 * Uses Cloud Function for authorization and manager validation
 */
export const approveTimeEntry = async (
  timeEntryId: string,
  reason?: string
): Promise<void> => {
  const timeEntryDoc = await getDoc(doc(db, 'TimeEntries', timeEntryId));

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const companyId = timeEntryDoc.data().companyId;

  const approveFn = httpsCallable(functions, 'approveTimeEntry');
  await approveFn({
    timeEntryId,
    companyId,
    ...(reason && { reason }),
  });
};

export const reopenTimeEntry = async (timeEntryId: string): Promise<void> => {
  const timeEntryDoc = await getDoc(doc(db, 'TimeEntries', timeEntryId));
  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }
  const companyId = timeEntryDoc.data().companyId;
  const reopenFn = httpsCallable(functions, 'reopenTimeEntry');
  await reopenFn({ timeEntryId, companyId });
};

/**
 * Correct clock-in/out times and approve a time entry
 * Uses Cloud Function for authorization and audit trail
 */
export const correctTimeEntry = async (
  timeEntryId: string,
  correctedClockIn: Date,
  correctedClockOut: Date | null,
  correctionReason: string
): Promise<void> => {
  const timeEntryDoc = await getDoc(doc(db, 'TimeEntries', timeEntryId));

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const companyId = timeEntryDoc.data().companyId;

  const correctFn = httpsCallable(functions, 'correctTimeEntry');
  await correctFn({
    timeEntryId,
    companyId,
    correctedClockIn: correctedClockIn.toISOString(),
    correctedClockOut: correctedClockOut?.toISOString() ?? undefined,
    correctionReason,
  });
};

export const createManualTimeEntry = async (params: {
  companyId: string;
  userId: string;
  oneTimeJobId: string;
  clockIn: Date;
  clockOut: Date;
  note: string;
}): Promise<void> => {
  const fn = httpsCallable(functions, 'createManualTimeEntry');
  await fn({
    companyId: params.companyId,
    userId: params.userId,
    oneTimeJobId: params.oneTimeJobId,
    clockIn: params.clockIn.toISOString(),
    clockOut: params.clockOut.toISOString(),
    note: params.note,
  });
};

/**
 * Bulk approve time entries
 * Uses Cloud Function for authorization and manager validation
 */
export const bulkApproveTimeEntries = async (
  timeEntryIds: string[]
): Promise<void> => {
  if (timeEntryIds.length === 0) {
    return;
  }

  // Get first time entry to retrieve companyId (all entries should be from same company)
  const timeEntryDoc = await getDoc(doc(db, 'TimeEntries', timeEntryIds[0]));

  if (!timeEntryDoc.exists()) {
    throw new Error('Time entry not found');
  }

  const companyId = timeEntryDoc.data().companyId;

  const bulkApproveFn = httpsCallable(functions, 'bulkApproveTimeEntries');
  await bulkApproveFn({
    timeEntryIds,
    companyId,
  });
};
